import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Shield, CheckCircle, XCircle, Clock, Hash, Beaker } from 'lucide-react';
import axios from 'axios';
import { API_BASE } from '@/lib/api';

const AGENT_COLORS = {
  data_manager: 'text-emerald-400', nca_agent: 'text-blue-400',
  qc_agent: 'text-red-400', report_agent: 'text-emerald-400',
  reg_intel_agent: 'text-sky-400', simulator_agent: 'text-orange-400',
  poppk_expert: 'text-purple-400', general: 'text-primary',
  agent_execution: 'text-muted-foreground',
};

export default function AuditTrailView({ sessionId }) {
  const [entries, setEntries] = useState([]);
  const [verification, setVerification] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (sessionId) loadAudit();
  }, [sessionId]);

  const loadAudit = async () => {
    try {
      const [auditRes, verifyRes] = await Promise.all([
        axios.get(`${API_BASE}/audit/${sessionId}`),
        axios.get(`${API_BASE}/audit/${sessionId}/verify`),
      ]);
      setEntries(auditRes.data.entries || []);
      setVerification(verifyRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Audit Trail</h2>
        {verification && (
          <div className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full ${
            verification.valid ? 'bg-green-500/15 text-green-400' : 'bg-red-500/15 text-red-400'
          }`}>
            {verification.valid ? <CheckCircle className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
            {verification.valid ? 'Chain Intact' : 'Chain Broken'}
            <span className="font-mono text-[10px]">({verification.entries} entries)</span>
          </div>
        )}
      </div>

      {loading ? (
        <div className="space-y-2">{[1,2,3,4].map(i => <Skeleton key={i} className="h-12" />)}</div>
      ) : entries.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center py-12">
            <Shield className="w-8 h-8 text-muted-foreground/30 mb-2" />
            <p className="text-sm text-muted-foreground">No audit entries yet</p>
            <p className="text-xs text-muted-foreground/50 mt-1">Actions will be logged here as you use the workspace</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/20">
                  <TableHead className="text-[10px] font-mono">Time</TableHead>
                  <TableHead className="text-[10px] font-mono">Agent</TableHead>
                  <TableHead className="text-[10px] font-mono">Action</TableHead>
                  <TableHead className="text-[10px] font-mono">Hash</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((entry, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-[10px] font-mono text-muted-foreground whitespace-nowrap">
                      <Clock className="w-3 h-3 inline mr-1" />
                      {new Date(entry.timestamp).toLocaleTimeString()}
                    </TableCell>
                    <TableCell>
                      <span className={`text-[10px] font-mono ${AGENT_COLORS[entry.agent] || 'text-muted-foreground'}`}>
                        {entry.agent}
                      </span>
                    </TableCell>
                    <TableCell className="text-[10px] text-muted-foreground max-w-[200px] truncate">
                      {entry.action}
                    </TableCell>
                    <TableCell className="text-[9px] font-mono text-muted-foreground/50">
                      <Hash className="w-3 h-3 inline mr-0.5" />
                      {entry.entry_hash?.slice(0, 12)}...
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}
